import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CategoryApiAdapter } from '../adapters/CategoryApiAdapter';
import { LocationsApiAdapter } from '../adapters/LocationsApiAdapter';
import { ProductsApiAdapter } from '../adapters/ProductsApiAdapter';
import { ShopApiAdapter } from '../adapters/ShopApiAdapter';

// Tipos para el hook
interface Stock {
  ubicacionId: string;
  codigo: string;
  tipo: string;
  tipoId: string;
  stock: number;
}

interface Link {
  tienda: any;
  producto: any;
  variante: any;
}

interface Location {
  value: string;
  label: string;
  typeName?: string;
  typeId?: string;
}

export const useProductForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getTeamType } = usePermissions();
  const queryClient = useQueryClient();
  const { id: productId } = useParams();
  const isTenant3pl = getTeamType() === 'tenant_3pl';
  const productDataRef = useRef<any>(null);

  // Estados del formulario
  const [initialData, setInitialData] = useState<any>(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [codigo, setCodigo] = useState('');
  const [coste, setCoste] = useState('');
  const [categoria, setCategoria] = useState('');
  const [imagenPreview, setImagenPreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [length, setLength] = useState('');
  const [heigth, setHeigth] = useState('');
  const [width, setWidth] = useState('');
  const [weight, setWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [dimensionUnit, setDimensionUnit] = useState('cm');

  const [batchActive, setBatchActive] = useState(false);
  const [batchType, setBatchType] = useState('');

  // Estados de datos externos
  const [ubicaciones, setUbicaciones] = useState<Location[]>([]);
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(true);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [enlaces, setEnlaces] = useState<Link[]>([]);

  // Cargar ubicaciones con stock por producto (solo modo edición)
  const httpClient = createHttpClient(apiClientWH);
  const locationsAdapter = new LocationsApiAdapter(httpClient);
  const { data: locationsWithStock } = useQuery({
    queryKey: ['locations-by-product', productId],
    enabled: Boolean(productId) && isTenant3pl,
    queryFn: () => locationsAdapter.getAll({ product_ids: String(productId || '') as any }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const stocksFromApi: Stock[] = Array.isArray(locationsWithStock)
    ? locationsWithStock.map((l: any) => ({
        ubicacionId: String(l.id),
        codigo: l.name,
        tipo: l.typeName,
        tipoId: String(l.typeId || ''),
        stock: typeof l.stock === 'number' ? l.stock : 0,
      }))
    : [];

  // Cargar datos del producto si estamos en modo edición
  useEffect(() => {
    if (!productId) return;
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new ProductsApiAdapter(httpClient);
    let cancelled = false;
    adapter
      .getById(productId)
      .then((data) => {
        if (cancelled) return;
        setInitialData(data);
        if (Array.isArray(data.linked_products)) {
          setEnlaces(data.linked_products);
        }
      })
      .catch(() => {
        // Evita UnhandledRejection; interceptores/Sentry ya registran el error
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Refetch manual de producto (para actualizar enlaces desde API)
  const refetchProduct = async () => {
    if (!productId) return;
    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ProductsApiAdapter(httpClient);
      const data = await adapter.getById(productId);
      setInitialData(data);
      if (Array.isArray(data.linked_products)) {
        setEnlaces(data.linked_products);
      }
    } catch {
      // Idem: no propagar rechazo no controlado
    }
  };

  const { data: categorias = [], isLoading: loadingCategorias } = useQuery({
    queryKey: ['products-create-edit-categories'],
    queryFn: async () => {
      const adapter = new CategoryApiAdapter(createHttpClient(apiClientWH));
      return adapter.getAll();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: shops = [] } = useQuery({
    queryKey: ['products-create-edit-shops'],
    queryFn: async () => {
      const adapter = new ShopApiAdapter(createHttpClient(apiClientWH));
      return adapter.getAll();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Cargar ubicaciones para añadir stock (solo API disponible / permitida en tenant_3pl)
  useEffect(() => {
    if (!isTenant3pl) {
      setUbicaciones([]);
      setLoadingUbicaciones(false);
      return;
    }

    const httpClient = createHttpClient(apiClientWH);
    const adapter = new LocationsApiAdapter(httpClient);
    setLoadingUbicaciones(true);

    const type_ids = '1,2';
    adapter
      .getAll({
        type_ids,
      })
      .then((data) => {
        setUbicaciones(
          data.map((u) => ({
            value: u.id,
            label: u.name,
            typeName: u.typeName,
            typeId: u.typeId,
          })),
        );
      })
      .catch(() => {
        setUbicaciones([]);
      })
      .finally(() => {
        setLoadingUbicaciones(false);
      });
  }, [isTenant3pl]);

  useEffect(() => {
    if (initialData) {
      setNombre(initialData.name || '');
      setDescripcion(initialData.description || '');
      setCodigo(initialData.code || '');
      setCoste(initialData.cost != null ? String(initialData.cost) : '');
      setCategoria(initialData.category_id ? String(initialData.category_id) : '');
      setImagenPreview(initialData.image_hash || null);
      setImageFile(null);
      setLength(initialData.length || '');
      setHeigth(initialData.heigth || '');
      setWidth(initialData.width || '');
      setWeight(initialData.weight || '');
      if (initialData.weight_unit) setWeightUnit(String(initialData.weight_unit));
      if (initialData.dimension_unit) setDimensionUnit(String(initialData.dimension_unit));
      setBatchActive(Boolean(initialData.batch_active));
      setBatchType(initialData.batch_type || '');
    }
  }, [initialData]);

  // Funciones de manejo de stock
  const handleAddStock = (data: { ubicacion: string; unidades: number }) => {
    const ubicacionObj = ubicaciones.find((u) => u.value === data.ubicacion);
    if (!ubicacionObj) {
      console.warn('Ubicación no válida seleccionada:', data.ubicacion);
      return;
    }

    const ubicacionLabel = ubicacionObj.label;
    const tipoUbicacion = ubicacionObj.typeName || 'N/A';
    const tipoId = ubicacionObj.typeId || '';

    setStocks((prev) => [
      ...prev,
      {
        ubicacionId: ubicacionObj.value,
        codigo: ubicacionLabel,
        tipo: tipoUbicacion,
        tipoId,
        stock: data.unidades,
      },
    ]);
  };

  const handleDeleteStock = (ubicacionId: string) => {
    setStocks((prev) => prev.filter((s) => s.ubicacionId !== ubicacionId));
  };

  // Funciones de manejo de enlaces
  const handleAddLink = (enlace: Link) => {
    console.log('Agregando enlace:', enlace);
    setEnlaces((prev) => [...prev, enlace]);
  };

  const handleDeleteLink = (index: number) => {
    setEnlaces((prev) => prev.filter((_, i) => i !== index));
  };

  // Filtrado de ubicaciones para el combobox
  const filtrarUbicaciones = (searchText = '') => {
    const ubicacionesSeleccionadas = stocks.map((s) => s.ubicacionId);
    return ubicaciones
      .filter((u) => ['1', '2'].includes(u.typeId || ''))
      .filter((u) => !ubicacionesSeleccionadas.includes(u.value))
      .filter((u) => u.label.toLowerCase().includes(searchText.toLowerCase()));
  };

  // Validación de campos obligatorios
  // TODO: consider migrating form validation to Zod or Yup schema
  const isSaveDisabled = !nombre || !codigo;

  // Función de navegación
  const handleCancel = () => {
    navigate('/products');
  };

  return {
    // Estados del formulario
    nombre,
    setNombre,
    descripcion,
    setDescripcion,
    codigo,
    setCodigo,
    coste,
    setCoste,
    categoria,
    setCategoria,
    imagenPreview,
    setImagenPreview,
    imageFile,
    setImageFile,
    length,
    setLength,
    heigth,
    setHeigth,
    width,
    setWidth,
    weight,
    setWeight,
    weightUnit,
    setWeightUnit,
    dimensionUnit,
    setDimensionUnit,
    batchActive,
    setBatchActive,
    batchType,
    setBatchType,

    // Estados de datos externos
    categorias,
    loadingCategorias,
    shops,
    ubicaciones,
    loadingUbicaciones,
    stocks,
    stocksFromApi,
    enlaces,
    initialData,

    // Referencias
    productDataRef,

    // Funciones de manejo
    handleAddStock,
    handleDeleteStock,
    handleAddLink,
    handleDeleteLink,
    filtrarUbicaciones,
    handleCancel,

    // Validaciones
    isSaveDisabled,

    // Utilidades
    productId,
    navigate,
    toast,
    queryClient,
    refetchProduct,
  };
};
